/**
 * Groups drivers into LOCAL / OTR blocks for the Planning - Week View
 * sub-grouping, mirroring the case-insensitive matching in
 * ./driverPreferenceFilter (safetynew.driver.preference holds 'LOCAL' / 'OTR',
 * but persisted user settings can still carry the older 'Local' spelling).
 */

export type PreferenceBucket = 'LOCAL' | 'OTR' | 'NONE';

export interface PreferenceGroup<T> {
  bucket: PreferenceBucket;
  label: string;
  drivers: T[];
}

const BUCKET_LABELS: Record<PreferenceBucket, string> = {
  LOCAL: 'LOCAL',
  OTR: 'OTR',
  NONE: 'No Preference',
};

// Display order for the sub-group headers
const BUCKET_ORDER: PreferenceBucket[] = ['LOCAL', 'OTR', 'NONE'];

export function getPreferenceBucket(preference: string | null | undefined): PreferenceBucket {
  const normalized = (preference || '').trim().toUpperCase();
  if (normalized === 'LOCAL') return 'LOCAL';
  if (normalized === 'OTR') return 'OTR';
  return 'NONE';
}

/**
 * Partitions drivers into LOCAL / OTR / No Preference groups.
 *
 * Buckets always come back in LOCAL, OTR, No Preference order, empty buckets
 * are omitted, and the incoming order is preserved within each bucket so the
 * caller's saved sort order survives.
 *
 * Pass a `getPreference` accessor when the items are wrappers around a driver
 * rather than the driver itself (the week view's flat rows carry the dispatcher
 * alongside the driver).
 */
export function groupByPreferenceBucket<T extends { preference?: string | null }>(
  items: T[]
): PreferenceGroup<T>[];
export function groupByPreferenceBucket<T>(
  items: T[],
  getPreference: (item: T) => string | null | undefined
): PreferenceGroup<T>[];
export function groupByPreferenceBucket<T>(
  items: T[],
  getPreference?: (item: T) => string | null | undefined
): PreferenceGroup<T>[] {
  const readPreference = getPreference
    ?? ((item: T) => (item as { preference?: string | null }).preference);
  const buckets = new Map<PreferenceBucket, T[]>();

  items.forEach(driver => {
    const bucket = getPreferenceBucket(readPreference(driver));
    const existing = buckets.get(bucket);
    if (existing) {
      existing.push(driver);
    } else {
      buckets.set(bucket, [driver]);
    }
  });

  return BUCKET_ORDER
    .filter(bucket => buckets.has(bucket))
    .map(bucket => ({
      bucket,
      label: BUCKET_LABELS[bucket],
      drivers: buckets.get(bucket) as T[],
    }));
}
